import { cancelUpload, uploadFile } from '@weblite/file'
import { IMessage, RawVideoMessage } from '@weblite/ts-action/message'
import { v4 as uuidV4 } from 'uuid'
import * as R from 'ramda'
import { PendingUploadVideoAttachment } from '$logic/pending-uploads/types'
import {
  editMessage,
  getAudioAttachmentFromFile,
  getMessageByIid,
  getVideoAttachmentFromFile,
} from '$logic/message'
import {
  deleteUploadingFile,
  getUploadingFile,
  saveUploadingFile,
} from '$logic/persist-uploads'
import { createPendingEdits, deletePendingEdits } from '$logic/pending-edits'
import { PendingEdit } from '$logic/pending-edits/types'
import { pendingEditUploadsStorage } from '$logic/pending-edit-uploads/storage'
import { PendingEditUpload } from '$logic/pending-edit-uploads/types'
import {
  getFileAttachmentFromFile,
  getImageMessageAttachmentFromFile,
} from '$logic/message/helper'
import { getConversion } from '$logic/pending-uploads'
import { getThumbnailUploadId } from '$logic/pending-uploads/helper'

const processingPendingEditUploads = new Set<string>()

export function createRawPendingEditUpload(
  message: IMessage,
  attachment: IMessage['attachment'],
  body?: string,
  hasThumbnail?: boolean,
) {
  const uploadId = uuidV4()
  return {
    id: message.id,
    type: message.type,
    roomId: message.roomId,
    uploadId,
    thumbnailUploadId: hasThumbnail
      ? getThumbnailUploadId(uploadId)
      : undefined,
    attachment,
    iid: message.iid,
    ...(body && { body }),
  }
}

export async function createPendingEditUpload(
  message: PendingEditUpload,
  file: File,
  thumbnailFile?: File,
) {
  if (message.type === 'IMAGE') await saveUploadingFile(message.uploadId, file)
  else saveUploadingFile(message.uploadId, file)

  if (thumbnailFile && message.thumbnailUploadId)
    await saveUploadingFile(message.thumbnailUploadId, thumbnailFile)

  pendingEditUploadsStorage.insert(message)

  handlePendingEditUpload(message, file, thumbnailFile)
}

export function deletePendingEditUpload(uploadId: string, messageId?: string) {
  if (messageId) pendingEditUploadsStorage.delete(messageId)
  cancelUpload(uploadId)
  cancelUpload(getThumbnailUploadId(uploadId))
  deleteUploadingFile(uploadId)
  deleteUploadingFile(getThumbnailUploadId(uploadId))
}

async function processUploadedEditMessage(
  message: PendingEditUpload,
  { fileId, size }: { size: number; fileId: string },
) {
  const attachment = {
    ...message.attachment,
    fileId,
    size,
  }

  const pendingEdit = {
    id: message.id,
    roomId: message.roomId,
    iid: message.iid,
    ...(message.body !== undefined && { body: message.body }),
    attachment,
  } as PendingEdit
  await createPendingEdits([pendingEdit])
  await deletePendingEditUpload(message.uploadId, message.id)

  await editMessage(pendingEdit).then(async () => {
    await deletePendingEdits([pendingEdit.id])
  })
}

async function handleCompleteVideoEditPendingUpload(uploadId: string) {
  const message = pendingEditUploadsStorage.get(uploadId)
  const attachment = message.attachment as RawVideoMessage['attachment']
  if (
    attachment.fileId &&
    attachment.thumbnailFileId &&
    message.conversionCompleted
  ) {
    await processUploadedEditMessage(message, {
      fileId: attachment.fileId,
      size: attachment.size,
    })
    await deleteUploadingFile(message.uploadId)
    await deleteUploadingFile(message.thumbnailUploadId!)
    pendingEditUploadsStorage.delete(message.id)
  }
}

async function updatePendingEditUpload(
  uploadId: string,
  update: (oldMessage: PendingEditUpload) => PendingEditUpload,
) {
  const tempMessage = pendingEditUploadsStorage.get(uploadId)
  const newMessage = update(tempMessage)
  await pendingEditUploadsStorage.update(newMessage)
}

export function handlePendingEditUpload(
  message: PendingEditUpload,
  file: File,
  thumbnailFile?: File,
) {
  processingPendingEditUploads.add(message.uploadId)
  if (
    thumbnailFile &&
    message.thumbnailUploadId &&
    !(message.attachment as RawVideoMessage['attachment']).thumbnailFileId
  ) {
    uploadFile({
      uploadId: message.thumbnailUploadId,
      file: thumbnailFile,
      type: 'infer',
      onComplete: async info => {
        await updatePendingEditUpload(message.uploadId, oldMessage =>
          R.assocPath(
            ['attachment', 'thumbnailFileId'],
            info.fileId,
            oldMessage,
          ),
        )
        handleCompleteVideoEditPendingUpload(message.uploadId)
      },
    })
  }
  uploadFile({
    uploadId: message.uploadId,
    file,
    type: 'infer',
    async onConversionProgress({ percent }) {
      if (message.thumbnailUploadId && percent === 100) {
        await updatePendingEditUpload(message.uploadId, oldMessage =>
          R.assoc('conversionCompleted', true, oldMessage),
        )
        handleCompleteVideoEditPendingUpload(message.uploadId)
      }
    },
    onComplete: async info => {
      if (message.thumbnailUploadId) {
        await updatePendingEditUpload(message.uploadId, oldMessage =>
          R.assoc(
            'attachment',
            {
              ...oldMessage.attachment,
              fileId: info.fileId,
              size: info.size,
            },
            oldMessage,
          ),
        )
        handleCompleteVideoEditPendingUpload(message.uploadId)
        return
      }
      await processUploadedEditMessage(message, info)
      await deleteUploadingFile(message.uploadId)
      processingPendingEditUploads.delete(message.id)
    },
    convert: getConversion(
      message,
      (message.attachment as PendingUploadVideoAttachment).compressionHeight,
    ),
    queue: message.roomId,
  })
}

export async function syncPendingEditUploads() {
  await Promise.all(
    pendingEditUploadsStorage.getAll().map(async message => {
      if (processingPendingEditUploads.has(message.uploadId)) return
      const file = await getUploadingFile(message.uploadId)
      if (!file) return
      if (message.thumbnailUploadId) {
        const thumbnailFile = await getUploadingFile(message.thumbnailUploadId)
        if (thumbnailFile) handlePendingEditUpload(message, file, thumbnailFile)
      } else handlePendingEditUpload(message, file)
    }),
  )
}

export async function editAudioMessage(
  roomId: string,
  iid: number,
  file: File,
  body?: string,
) {
  const pendingAttachment = await getAudioAttachmentFromFile(file)
  const message = await getMessageByIid(roomId, iid)
  const pendingEditUpload = createRawPendingEditUpload(
    message as IMessage,
    pendingAttachment,
    body,
  )
  createPendingEditUpload(pendingEditUpload, file)
}

export async function editImageMessage(
  roomId: string,
  iid: number,
  file: File,
  body?: string,
) {
  const { attachment, image } = await getImageMessageAttachmentFromFile(file)
  const message = await getMessageByIid(roomId, iid)
  const pendingEditUpload = createRawPendingEditUpload(
    message as IMessage,
    attachment,
    body,
  )
  createPendingEditUpload(pendingEditUpload, image)
}

export async function editVideoMessage(
  roomId: string,
  iid: number,
  file: File,
  compressionHeight: number,
  body?: string,
) {
  const {
    pendingAttachment,
    videoHighQualityThumbnail,
  } = await getVideoAttachmentFromFile(file, compressionHeight)
  const message = await getMessageByIid(roomId, iid)

  const pendingEditUpload = createRawPendingEditUpload(
    message as IMessage,
    pendingAttachment,
    body,
    true,
  )

  createPendingEditUpload(pendingEditUpload, file, videoHighQualityThumbnail)
}

export async function editFileMessage(
  roomId: string,
  iid: number,
  file: File,
  body?: string,
) {
  const pendingAttachment = getFileAttachmentFromFile(file)
  const message = await getMessageByIid(roomId, iid)
  const pendingEditUpload = createRawPendingEditUpload(
    message as IMessage,
    pendingAttachment,
    body,
  )
  createPendingEditUpload(pendingEditUpload, file)
}
